import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@structural-climbing/ui'
import { NavIcon } from '../layouts/NavIcon'
import { useTopBarSlot } from '../layouts/topBarSlot'

interface PrimaryAction {
  label: string
  onPress: () => void
  isDisabled?: boolean
}

interface Props {
  title: ReactNode
  subtitle?: ReactNode
  /**
   * The page's one "create" action (Add member, New class, Invite). A button on
   * the right on desktop; on a phone it moves into the top bar as a "+".
   */
  primaryAction?: PrimaryAction
  /** Other controls beside the title. They stay in the page on every screen. */
  actions?: ReactNode
}

/**
 * The header every gym page opens with: title, one-line subtitle, actions on
 * the right. On a phone the top bar already names the page, so the title is
 * kept for screen readers only and the page starts with its content.
 */
export function PageHeader({ title, subtitle, primaryAction, actions }: Props) {
  const topBarSlot = useTopBarSlot()
  const onPhone = topBarSlot !== null
  const showActionRow = Boolean(actions) || (primaryAction && !onPhone)

  return (
    <header
      className={`flex flex-col gap-3 md:flex-row md:items-end md:justify-between ${
        onPhone && !showActionRow ? '' : 'mb-6'
      }`}
    >
      <div className={onPhone ? 'sr-only' : 'min-w-0'}>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-foreground/60">{subtitle}</p>}
      </div>
      {showActionRow && (
        <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:justify-end">
          {actions}
          {primaryAction && !onPhone && (
            <Button
              color="primary"
              isDisabled={primaryAction.isDisabled}
              startContent={<NavIcon name="plus" size={16} />}
              onPress={primaryAction.onPress}
            >
              {primaryAction.label}
            </Button>
          )}
        </div>
      )}
      {primaryAction &&
        topBarSlot &&
        createPortal(
          <Button
            isIconOnly
            size="sm"
            color="primary"
            aria-label={primaryAction.label}
            isDisabled={primaryAction.isDisabled}
            onPress={primaryAction.onPress}
          >
            <NavIcon name="plus" size={18} />
          </Button>,
          topBarSlot,
        )}
    </header>
  )
}
